import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Share2, Sparkles, Store } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AvatarPicker } from "../components/AvatarPicker";
import { OndaAvatar } from "../components/OndaAvatar";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { PreparedBadge } from "../components/badges/PreparedBadge";
import { usePreparedBadge } from "../hooks/usePreparedBadge";
import { formatApiError } from "../lib/api";
import { shareOrCopy } from "../lib/share";

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [busy, setBusy] = useState(false);
  const prepared = usePreparedBadge(user?.emergency_profile_complete);

  const isBiz = user?.is_verified_business;
  const [autoOn, setAutoOn] = useState(user?.auto_reply_enabled || false);
  const [autoText, setAutoText] = useState(
    user?.auto_reply_text || "¡Hola! Gracias por escribirnos. ¿En qué te ayudamos?",
  );
  const [savingBiz, setSavingBiz] = useState(false);

  if (!user) return null;

  const onSave = async (e) => {
    e.preventDefault();
    if (displayName.trim().length < 1) {
      toast.error("El nombre visible no puede estar vacío.");
      return;
    }
    setBusy(true);
    try {
      await updateProfile({ display_name: displayName.trim(), bio: bio.trim(), avatar });
      toast.success("Perfil actualizado. ¡Se ve chido!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onSaveBiz = async () => {
    if (autoText.trim().length < 1) {
      toast.error("La respuesta automática no puede estar vacía.");
      return;
    }
    setSavingBiz(true);
    try {
      await updateProfile({ auto_reply_enabled: autoOn, auto_reply_text: autoText.trim() });
      toast.success("Configuración de negocio guardada.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingBiz(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-24 pb-28 q-page-enter" data-testid="profile-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 mb-4"
        data-testid="profile-back"
      >
        <ArrowLeft size={16} /> Volver
      </button>

      <div className="q-glass rounded-3xl p-6 sm:p-8">
        <div className="flex items-center gap-4">
          <OndaAvatar src={avatar} name={displayName} size={80} testId="profile-avatar-preview" />
          <div className="flex-1 min-w-0">
            <p className="q-chip">
              Editando perfil
              {isBiz && <VerifiedBadge size={11} testId="profile-verified-chip" />}
            </p>
            <h1 className="font-display text-3xl font-bold mt-2 flex items-center gap-1">
              Tu perfil
              {isBiz && <VerifiedBadge size={20} testId="profile-verified-badge" />}
            </h1>
            <p className="font-mono text-xs text-slate-500 mt-1">@{user.ondatag} · {user.email}</p>
            {prepared && (
              <div className="mt-2">
                <PreparedBadge testId="profile-prepared-badge" />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() =>
              shareOrCopy({
                title: "Qué Onda Chat",
                text: `¡Únete a Qué Onda Chat! Encuéntrame como @${user.ondatag}`,
                url: typeof window !== "undefined" ? window.location.origin : "",
                ref: user.ondatag,
              })
            }
            className="q-btn-ghost !py-2 !px-3 inline-flex items-center gap-1.5 text-sm shrink-0"
            data-testid="profile-share"
            aria-label="Compartir Qué Onda Chat"
          >
            <Share2 size={14} />
            <span className="hidden sm:inline">Compartir</span>
          </button>
        </div>

        <form onSubmit={onSave} className="mt-6 space-y-5" data-testid="profile-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
              Nombre visible
            </label>
            <input
              data-testid="profile-display-name"
              className="q-input mt-1"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={40}
              required
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
              Biografía
              <span className="ml-2 text-slate-500 normal-case tracking-normal">
                ({bio.length}/200)
              </span>
            </label>
            <textarea
              data-testid="profile-bio"
              className="q-input mt-1 min-h-[96px] resize-none"
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 200))}
              maxLength={200}
              placeholder="Cuéntanos algo cortito sobre ti..."
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-display block mb-2">
              Avatar
            </label>
            <AvatarPicker
              selected={avatar}
              onSelect={setAvatar}
              name={displayName}
              testIdPrefix="profile-avatar-option"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-2xl border border-white/10 bg-black/20 p-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                @OndaTag
              </div>
              <div
                className="font-mono text-sm text-slate-200 mt-1"
                data-testid="profile-ondatag-readonly"
              >
                @{user.ondatag}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                Correo
              </div>
              <div className="text-sm text-slate-200 mt-1 truncate" data-testid="profile-email-readonly">
                {user.email}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="q-btn-primary inline-flex items-center gap-2"
            data-testid="profile-save"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar cambios
          </button>
        </form>
      </div>

      {isBiz && (
        <div className="q-glass rounded-3xl p-6 sm:p-8 mt-4" data-testid="business-settings">
          <div className="flex items-center gap-2 mb-1">
            <Store size={18} className="q-accent-text" />
            <h2 className="font-display text-xl font-bold">Automatización de negocio</h2>
            <VerifiedBadge size={16} />
          </div>
          <p className="text-xs text-slate-400 mb-4">
            Activa una respuesta automática de bienvenida cuando alguien te escriba por primera vez.
          </p>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={autoOn}
              onChange={(e) => setAutoOn(e.target.checked)}
              data-testid="business-auto-toggle"
            />
            <span
              className={
                "relative inline-block w-11 h-6 rounded-full transition-colors " +
                (autoOn ? "q-accent-bg" : "bg-slate-700")
              }
            >
              <span
                className={
                  "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform " +
                  (autoOn ? "translate-x-5" : "translate-x-0")
                }
              />
            </span>
            <span className="text-sm text-slate-200 font-display font-semibold flex items-center gap-1">
              <Sparkles size={14} /> Respuesta automática de bienvenida
            </span>
          </label>

          <div className="mt-4">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-display">
              Texto de bienvenida
              <span className="ml-2 text-slate-500 normal-case tracking-normal">
                ({autoText.length}/200)
              </span>
            </label>
            <textarea
              className="q-input mt-1 min-h-[80px] resize-none"
              value={autoText}
              onChange={(e) => setAutoText(e.target.value.slice(0, 200))}
              maxLength={200}
              disabled={!autoOn}
              data-testid="business-auto-text"
            />
          </div>

          <button
            type="button"
            onClick={onSaveBiz}
            disabled={savingBiz}
            className="q-btn-primary inline-flex items-center gap-2 mt-4"
            data-testid="business-save"
          >
            {savingBiz ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar configuración de negocio
          </button>
        </div>
      )}
    </div>
  );
}
